import { useLocation, useNavigate } from 'react-router-dom';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useMergeSuggestionStore } from '@/store/useMergeSuggestionStore';
import { getDefaultRoute } from '@/lib/defaultRoute';
import { AccountMergeDialog } from './AccountMergeDialog';

/**
 * App-wide host for the account-merge dialog. Mounted once in RootLayout —
 * inside the data router (Go Back navigates; /view suppression reads the
 * location) and above both the public and protected trees, so login-time
 * suggestions (SignIn / Sso / SignUp all write the fresh user via signIn())
 * and mid-session auth-me refreshes surface through the SAME component with
 * no per-page wiring, and the dialog survives route changes.
 *
 * Sources, denial first (richer and more urgent):
 * - DENIAL: `useMergeSuggestionStore.denial`, set by the API client's 403
 *   interceptor when a denial carries a merge_suggestion envelope.
 * - PROACTIVE: derived directly from `user.mergeSuggestion` — auth/me is
 *   already reactive server state and only ever carries non-ignored
 *   suggestions. Resolution clears it at the source (merge → /auth/me
 *   reload has no suggestion; Ignore → updateCurrentUser clears it), so
 *   presence alone decides visibility and there is no dismissed-state to
 *   track (the dialog is a forced binary choice).
 *
 * The /view (consume) area suppresses the PROACTIVE channel only. That
 * decision (2026-07-16) was about not interrupting a viewer mid-consume with
 * "you may have another account" — which still holds. It never applied to a
 * denial: a viewer stuck on a 403 is not being interrupted, and the merge is
 * the only thing that unblocks the page they are looking at. vingapi's consume
 * gate now delivers a CONTENT-grant suggestion for exactly that case (a
 * contested representation — a contact carrying this person's verified method
 * that the org also attaches to another account), so withholding it here would
 * strand the one population that cannot resolve it any other way.
 */
export function AccountMergeHost() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  const denial = useMergeSuggestionStore((s) => s.denial);
  const clearDenial = useMergeSuggestionStore((s) => s.clearDenial);
  // #223 defect B/C: suggestions this session has already finished with
  // (dismissed, ignored, or 404-gone). /auth/me can keep sending them — the
  // backend deliberately re-surfaces ignored suggestions — but re-opening the
  // same id in the same session is the loop, not the feature.
  const dismissedIds = useMergeSuggestionStore((s) => s.dismissedIds);

  const isConsumeRoute =
    location.pathname === '/view' || location.pathname.startsWith('/view/');

  if (denial && user) {
    const contextName =
      user.context?.type === 'network'
        ? (user.context.network?.name ?? null)
        : (user.context?.organization?.name ?? null);
    const handleGoBack = () => {
      clearDenial();
      // Real navigation, not a dismiss-in-place: leave the page that can't
      // load. History back when this app put us here; otherwise (deep link,
      // fresh tab) the user's default home.
      if (location.key !== 'default') {
        void navigate(-1);
      } else {
        // keep-search-exempt: merge-denial-go-home
        void navigate(getDefaultRoute(user), { replace: true });
      }
    };
    return (
      <AccountMergeDialog
        key={`denial-${denial.suggestion.id}`}
        variant="denial"
        suggestion={denial.suggestion}
        code={denial.code}
        contextName={contextName}
        onGoBack={handleGoBack}
        onResolved={clearDenial}
      />
    );
  }

  // Proactive only: never interrupt a viewer mid-consume (2026-07-16).
  // Positional suppression — it appears when they leave /view.
  if (isConsumeRoute) {
    return null;
  }

  // A suggestion with no verify methods cannot be acted on: the fork step IS
  // the list of methods to prove control at, and this dialog is a
  // forced binary only in its denial variant; the proactive variant is
  // dismissible by design (7acf43063). With no methods the fork step has
  // nothing to offer, so the modal would be a dead end whose one action is
  // Ignore.
  //
  // That is not hypothetical. vingapi suppresses `verify_methods` on a
  // PROVISIONAL merge — the row `POST /users/merge/claim/` creates from an
  // unproven assertion — so it cannot disclose the other account's method
  // list (identity-plan.md §7, user_routers.py). The claimant sees their own
  // provisional row, so once /auth/me applies that same suppression (vingapi
  // uptake #4250 — today it leaks the list instead), this channel would open
  // an inescapable dialog over the claim the user is in the middle of, whose
  // one button ignores it. The claim has its own dialog and its own code
  // step; nothing here can add to it.
  //
  // The proactive id comes from `/auth/me`'s `merge_suggestion` and nowhere
  // else — never from a denial envelope, a list endpoint, or a remembered
  // value — so the ignore/verify calls can only ever address a suggestion the
  // server just said this user has.
  const proactive = user?.mergeSuggestion ?? null;
  if (
    proactive &&
    proactive.verifyMethods.length > 0 &&
    !dismissedIds.includes(proactive.id)
  ) {
    return (
      <AccountMergeDialog
        key={`proactive-${proactive.id}`}
        variant="proactive"
        suggestion={proactive}
      />
    );
  }

  return null;
}
